<script lang="ts" setup>
import { useDialog, ref, type UserTier } from "#imports";
import MPAuth from "../dialogs/MPAuth/MPAuth.vue";
import MPButton from "../forms/MPButton.vue";
import type { SpotVisibility } from "~/stores/spots";

const { openDialog } = useDialog();

const props = withDefaults(
  defineProps<{
    visibility?: SpotVisibility;
  }>(),
  {
    visibility: "public",
  },
);

const emit = defineEmits<{
  subscribe: [value: UserTier];
  close: [];
}>();

const overlay = ref(null);
</script>
<template>
  <div class="preview-overlay-layer" ref="overlay">
    <div>
      <i class="ri-close-line close-icon" @click="emit('close')" />
    </div>
    <div class="preview-content">
      <div v-if="props.visibility === 'authenticated'" class="auth-content">
        <div>
          <MPImg src="/media/images/lock-red.svg" class="lock-vector" />
          <h2>See More</h2>
          <p>
            Join to explore and save handpicked spots, collected by creators you
            trust.
          </p>
        </div>
        <div class="buttons-container">
          <MPButton
            label="Login"
            size="large"
            link
            version="secondary"
            backgroundColor="var(--color-rose-500)"
            iconColor="var(--color-white)"
            @click="openDialog(MPAuth, { startOn: 'login' })"
          />
        </div>
      </div>
      <div v-if="props.visibility === 'subscriber'">
        <div>
          <MPImg src="/media/images/lock.svg" class="lock-vector" />
          <h2>Premium Spot</h2>
          <p>
            Dive deeper into this location and get full access to creators maps
            and hidden gems.
          </p>
        </div>
        <div class="buttons-container">
          <MPButton
            label="Upgrade to Premium"
            version="primary"
            backgroundColor="var(--color-indigo-600)"
            iconColor="var(--color-rose-50)"
            size="large"
            @click="emit('subscribe', 'premium')"
          />
        </div>
      </div>
      <div v-if="props.visibility === 'supporter'">
        <div>
          <MPImg src="/media/images/lock-green.svg" class="lock-vector" />
          <h2>Exclusive Spot</h2>
          <p>Support this creator and be part of their inner circle.</p>
        </div>
        <div class="buttons-container">
          <MPButton
            label="Support this creator"
            version="primary"
            backgroundColor="var(--color-green-500)"
            iconColor="var(--color-green-50)"
            size="large"
            @click="emit('subscribe', 'supporter')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.preview-overlay-layer {
  position: relative;
  z-index: 100;
  height: 100%;
  border-radius: 1rem;
  overflow-y: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  display: flex;
  &::-webkit-scrollbar {
    display: none;
  }
}

.preview-content {
  margin: 0 auto;
  max-width: 40rem;
  place-self: center;
  padding: 0 4rem;
  color: var(--color-indigo-50);
  text-align: center;

  .lock-vector {
    margin: 0 auto;
  }

  h2 {
    margin-top: 0rem;
    margin-bottom: 0.5rem;
    font-size: 30px;
    font-weight: 700;
    line-height: 105%;
    color: var(--color-indigo-50);
  }

  p {
    max-width: 30rem;
    margin: 0 auto;
    line-height: 120%;
  }

  .buttons-container {
    margin-top: 1.5rem;
    display: flex;
    gap: 1rem;
    place-content: center;
  }
}

.close-icon {
  color: white;
  font-size: 32px;
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
}
</style>
